const PAD = 10;
const NAV_PAD = 10;
const CALLOUT_W = 268;
const CALLOUT_H = 108;
const CALLOUT_H_ACTION = 132;
const GAP = 22;

export function isNavSelector(selector) {
  return /data-act-nav=/.test(String(selector || ""));
}

function coachRightInset() {
  const root = document.documentElement;
  const styles = getComputedStyle(root);
  const trn =
    parseInt(styles.getPropertyValue("--baysport-trn-right"), 10) ||
    parseInt(root.style.getPropertyValue("--baysport-trn-right"), 10) ||
    0;
  const act =
    parseInt(styles.getPropertyValue("--baysport-act-right"), 10) ||
    parseInt(root.style.getPropertyValue("--baysport-act-right"), 10) ||
    0;
  return Math.max(trn, act, 0);
}

export function measureTourTarget(selector) {
  if (!selector) return null;
  const el =
    document.querySelector(`${selector}[data-act-active="1"]`) ||
    document.querySelector(selector);
  if (!el) return null;
  const r = el.getBoundingClientRect();
  if (r.width < 2 || r.height < 2) return null;

  const pad = isNavSelector(selector) ? NAV_PAD : PAD;
  const hole = {
    top: Math.max(0, r.top - pad),
    left: Math.max(0, r.left - pad),
    width: r.width + pad * 2,
    height: r.height + pad * 2,
  };

  return {
    top: r.top,
    left: r.left,
    right: r.right,
    bottom: r.bottom,
    width: r.width,
    height: r.height,
    centerX: r.left + r.width / 2,
    centerY: r.top + r.height / 2,
    hole,
    isNav: isNavSelector(selector) || r.left < 280,
  };
}

function clamp(value, min, max) {
  return Math.min(Math.max(value, min), max);
}

function fitsRight(target, vw, inset) {
  return target.right + GAP + CALLOUT_W <= vw - inset - 16;
}

function fitsLeft(target, inset) {
  return target.left - GAP - CALLOUT_W >= 16 + inset;
}

function fitsBottom(target, vh, inset) {
  return target.bottom + GAP + CALLOUT_H <= vh - inset - 16;
}

function fitsTop(target) {
  return target.top - GAP - CALLOUT_H >= 16;
}

export function layoutTourCallout(target, { selector, compact = true } = {}) {
  if (!target) return null;
  const vw = window.innerWidth;
  const vh = window.innerHeight;
  const inset = coachRightInset();
  const isNav = target.isNav || isNavSelector(selector);
  const calloutH = compact ? CALLOUT_H : CALLOUT_H_ACTION;
  const tabBarZone = vh - 88;

  let placement = "right";
  let top = target.centerY - calloutH / 2;
  let left = target.right + GAP;

  if (isNav && target.bottom > tabBarZone) {
    placement = "top";
    top = Math.max(16, target.top - GAP - calloutH);
    left = clamp(
      target.centerX - CALLOUT_W / 2,
      16,
      vw - CALLOUT_W - inset - 16,
    );
  } else if (isNav && target.left < 320) {
    placement = "right";
    left = clamp(target.right + GAP + 12, 252, vw - CALLOUT_W - inset - 16);
    top = clamp(target.centerY - calloutH / 2, 16, vh - calloutH - 16);
  } else if (fitsRight(target, vw, inset)) {
    placement = "right";
    left = target.right + GAP;
    top = clamp(target.centerY - calloutH / 2, 16, vh - calloutH - 16);
  } else if (fitsLeft(target, inset)) {
    placement = "left";
    left = target.left - GAP - CALLOUT_W;
    top = clamp(target.centerY - calloutH / 2, 16, vh - calloutH - 16);
  } else if (fitsBottom(target, vh, inset)) {
    placement = "bottom";
    top = target.bottom + GAP;
    left = clamp(
      target.centerX - CALLOUT_W / 2,
      16,
      vw - CALLOUT_W - inset - 16,
    );
  } else if (fitsTop(target)) {
    placement = "top";
    top = target.top - GAP - calloutH;
    left = clamp(
      target.centerX - CALLOUT_W / 2,
      16,
      vw - CALLOUT_W - inset - 16,
    );
  } else {
    placement = "center";
    left = clamp((vw - CALLOUT_W - inset) / 2, 16, vw - CALLOUT_W - inset - 16);
    top = clamp(vh * 0.18, 16, vh - calloutH - 16);
  }

  const callout = {
    top,
    left,
    width: CALLOUT_W,
    height: calloutH,
    placement,
    anchorX: 0,
    anchorY: 0,
    tailTop: calloutH / 2,
  };

  const edgePad = 24;

  if (placement === "right") {
    callout.anchorX = callout.left;
    callout.anchorY = clamp(
      target.centerY,
      callout.top + edgePad,
      callout.top + calloutH - edgePad,
    );
    callout.tailTop = callout.anchorY - callout.top;
  } else if (placement === "left") {
    callout.anchorX = callout.left + CALLOUT_W;
    callout.anchorY = clamp(
      target.centerY,
      callout.top + edgePad,
      callout.top + calloutH - edgePad,
    );
    callout.tailTop = callout.anchorY - callout.top;
  } else if (placement === "bottom") {
    callout.anchorX = clamp(
      target.centerX,
      callout.left + edgePad,
      callout.left + CALLOUT_W - edgePad,
    );
    callout.anchorY = callout.top;
    callout.tailLeft = callout.anchorX - callout.left;
  } else if (placement === "top") {
    callout.anchorX = clamp(
      target.centerX,
      callout.left + edgePad,
      callout.left + CALLOUT_W - edgePad,
    );
    callout.anchorY = callout.top + calloutH;
    callout.tailLeft = callout.anchorX - callout.left;
  } else {
    callout.anchorX = callout.left + CALLOUT_W / 2;
    callout.anchorY = callout.top + calloutH;
  }

  const arrowEnd = pickArrowEnd(target, placement);
  const arrowPath = buildArrow(
    callout.anchorX,
    callout.anchorY,
    arrowEnd.x,
    arrowEnd.y,
    placement,
    isNav,
  );

  return { callout, arrowPath, hole: target.hole, placement };
}

function pickArrowEnd(target, placement) {
  if (placement === "right") {
    return { x: target.right + 2, y: target.centerY };
  }
  if (placement === "left") {
    return { x: target.left - 2, y: target.centerY };
  }
  if (placement === "bottom") {
    return { x: target.centerX, y: target.bottom + 2 };
  }
  if (placement === "top") {
    return { x: target.centerX, y: target.top - 2 };
  }
  return { x: target.centerX, y: target.centerY };
}

function buildArrow(x1, y1, x2, y2, placement, isNav) {
  const dx = x2 - x1;
  const dy = y2 - y1;

  if (isNav && (placement === "right" || placement === "left")) {
    const c1x = x1 + dx * 0.42;
    const c1y = y1;
    const c2x = x2 - dx * 0.18;
    const c2y = y2;
    return `M ${x1.toFixed(1)} ${y1.toFixed(1)} C ${c1x.toFixed(1)} ${c1y.toFixed(1)}, ${c2x.toFixed(1)} ${c2y.toFixed(1)}, ${x2.toFixed(1)} ${y2.toFixed(1)}`;
  }

  if (Math.abs(dx) > Math.abs(dy)) {
    const c1x = x1 + dx * 0.5;
    const c1y = y1;
    const c2x = x2 - dx * 0.2;
    const c2y = y2;
    return `M ${x1.toFixed(1)} ${y1.toFixed(1)} C ${c1x.toFixed(1)} ${c1y.toFixed(1)}, ${c2x.toFixed(1)} ${c2y.toFixed(1)}, ${x2.toFixed(1)} ${y2.toFixed(1)}`;
  }

  const c1x = x1;
  const c1y = y1 + dy * 0.45;
  const c2x = x2;
  const c2y = y2 - dy * 0.2;
  return `M ${x1.toFixed(1)} ${y1.toFixed(1)} C ${c1x.toFixed(1)} ${c1y.toFixed(1)}, ${c2x.toFixed(1)} ${c2y.toFixed(1)}, ${x2.toFixed(1)} ${y2.toFixed(1)}`;
}
